import { WZAttachment, WZDocument, WZItem, WZStatus } from '../types/wz';
import { normalizeTransportRoutes, TransportRouteSummaryDto } from './transport';
import { normalizeConversionUnit } from '../utils/dimensionConversion';
import { Customer, getDisplayOrderNumber } from '../types/order';
import { toWzUnitCode } from '../utils/wzVariantSelection';
import {
  asArray,
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { roundWarehouseQuantity } from '../utils/warehouseQuantity';
import { getPersistedQuantityForUnit } from '../utils/commercialQuantity';

const WZ_STATUSES: WZStatus[] = [
  WZStatus.DRAFT,
  WZStatus.CONFIRMED,
  WZStatus.RELEASED,
  WZStatus.RECEIVED,
  WZStatus.CANCELLED,
];

// Dimensions (mm) of the linked order's products, used to compute the live
// per-line m²/m³ conversion on the WZ form (mirrors the backend PDF resolution).
export interface WZOrderProductDimensions {
  name: string;
  unit?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  productCategoryDimensionDisplayProfile?: string | null;
}

export interface WZDocumentDto extends WZDocument {
  order: WZDocument['order'];
  items: WZItem[];
  attachments: WZAttachment[];
  linkedTransports: TransportRouteSummaryDto[];
  transportIds: string[];
  orderProducts: WZOrderProductDimensions[];
  itemsLoaded: boolean;
  attachmentsLoaded: boolean;
  orderNumber: string;
  /** Canonical operator-facing OMS number (manual number when active, raw otherwise). */
  displayOrderNumber: string;
  orderCustomerName: string;
  createdByName: string;
}

export interface WZPrefillItemDto {
  productVariantId: string | null;
  productName: string;
  productCode: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit: string;
  unitPrice?: number;
  totalValue?: number;
  saleQuantity?: number | null;
  saleUnit?: string | null;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  description?: string;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
}

export interface WZPrefillForOrderDto {
  items: WZPrefillItemDto[];
  orderHasProducts: boolean;
}

export function normalizeWZPrefillForOrder(raw: unknown): WZPrefillForOrderDto {
  const record = asRecord(raw);
  return {
    items: asArray(record?.items, (item) => {
      const row = asRecord(item);
      return {
        productVariantId: asOptionalString(row?.productVariantId) ?? null,
        productName: asString(row?.productName),
        productCode: asOptionalString(row?.productCode) || '',
        quantity: asNumber(row?.quantity),
        ...(row?.quantityPcs == null ? {} : { quantityPcs: asNumber(row.quantityPcs) }),
        ...(row?.quantityM2 == null ? {} : { quantityM2: asNumber(row.quantityM2) }),
        ...(row?.quantityM3 == null ? {} : { quantityM3: asNumber(row.quantityM3) }),
        unit: toWzUnitCode(asOptionalString(row?.unit)),
        unitPrice: row?.unitPrice == null ? undefined : asNumber(row.unitPrice),
        totalValue: row?.totalValue == null ? undefined : asNumber(row.totalValue),
        saleQuantity: row?.saleQuantity == null ? null : asNumber(row.saleQuantity),
        saleUnit: asOptionalString(row?.saleUnit) ?? null,
        conversionUnit: normalizeConversionUnit(row?.conversionUnit),
        ...(row?.conversionQuantity == null ? {} : { conversionQuantity: asNumber(row.conversionQuantity) }),
        description: asOptionalString(row?.description),
        width: row?.width == null ? null : asNumber(row.width),
        height: row?.height == null ? null : asNumber(row.height),
        length: row?.length == null ? null : asNumber(row.length),
        thickness: row?.thickness == null ? null : asNumber(row.thickness),
      };
    }),
    orderHasProducts: record?.orderHasProducts === true,
  };
}

export function normalizeWZDocument(raw: unknown, entity = 'wzDocument'): WZDocumentDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const order = normalizeWZOrder(record?.order, `${entity}.order`);
  const orderProducts = normalizeWZOrderProducts(asRecord(record?.order)?.products);
  const createdBy = normalizeCreatedBy(record?.createdBy, `${entity}.createdBy`);
  const items = asOptionalArray(record?.items, (item, index) => normalizeWZItem(item, `${entity}.items[${index}]`));
  const attachments = asOptionalArray(
    record?.attachments,
    (item, index) => normalizeWZAttachment(item, `${entity}.attachments[${index}]`),
  );
  const linkedTransports = normalizeTransportRoutes(record?.linkedTransports, `${entity}.linkedTransports`);
  const transportIds = asOptionalArray(record?.transportIds, (value) => asString(value))
    ?? linkedTransports.map((transport) => transport.id).filter(Boolean);

  return {
    id: asString(record?.id),
    wzNumber: asString(record?.wzNumber),
    issueDate: asString(record?.issueDate),
    releaseDate: asOptionalString(record?.releaseDate),
    receivedDate: asOptionalString(record?.receivedDate),
    orderId: asString(record?.orderId),
    order,
    recipientName: asString(record?.recipientName),
    recipientCompany: asOptionalString(record?.recipientCompany),
    recipientAddress: asString(record?.recipientAddress),
    recipientCity: asString(record?.recipientCity),
    recipientPostalCode: asString(record?.recipientPostalCode),
    recipientCountry: asString(record?.recipientCountry),
    recipientPhone: asOptionalString(record?.recipientPhone),
    recipientEmail: asOptionalString(record?.recipientEmail),
    recipientNIP: asOptionalString(record?.recipientNIP),
    deliveryAddress: asOptionalString(record?.deliveryAddress),
    transportCompany: asOptionalString(record?.transportCompany),
    driverName: asOptionalString(record?.driverName),
    vehicleRegistration: asOptionalString(record?.vehicleRegistration),
    transportNotes: asOptionalString(record?.transportNotes),
    transportPrice: record?.transportPrice == null ? undefined : Number(record.transportPrice),
    transportInvoiceNo: asOptionalString(record?.transportInvoiceNo),
    clientOrderNumber: asOptionalString(record?.clientOrderNumber),
    producerInvoice: asOptionalString(record?.producerInvoice),
    purchaseInvoiceAmount:
      record?.purchaseInvoiceAmount == null ? undefined : Number(record.purchaseInvoiceAmount),
    costOfGoodsSold: record?.costOfGoodsSold == null ? undefined : Number(record.costOfGoodsSold),
    salesInvoiceNumber: asOptionalString(record?.salesInvoiceNumber) ?? null,
    currency: asOptionalString(record?.currency),
    issuedBy: asOptionalString(record?.issuedBy),
    receivedBy: asOptionalString(record?.receivedBy),
    status: normalizeWZStatus(record?.status, `${entity}.status`),
    notes: asOptionalString(record?.notes),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
    createdBy,
    items: items ?? [],
    attachments: attachments ?? [],
    linkedTransports,
    transportIds,
    orderProducts,
    itemsLoaded: items !== undefined,
    attachmentsLoaded: attachments !== undefined,
    orderNumber: order?.orderNumber || '',
    displayOrderNumber: order?.displayOrderNumber || order?.orderNumber || '',
    orderCustomerName: order?.customer?.name || '',
    createdByName: createdBy?.email || '',
  };
}

export function normalizeWZDocuments(raw: unknown, entity = 'wzDocuments'): WZDocumentDto[] {
  return asArray(raw, (item, index) => normalizeWZDocument(item, `${entity}[${index}]`));
}

export function prepareWZItemsForSave(items: Partial<WZItem>[]) {
  return items
    .filter(item => item.productName)
    .map(item => ({
      ...item,
      netWeight: item.netWeight ?? item.weight,
      grossWeight: item.grossWeight ?? item.weight,
    }));
}

function normalizeWZQuantity(value?: number) {
  const quantity = Number(value);
  if (!Number.isFinite(quantity)) {
    return value;
  }

  return roundWarehouseQuantity(quantity);
}

// Single home for "Customer -> WZ recipient block" field mapping so the order
// auto-fill (modules/wz/api/wzCreateApi.ts) and the manual recipient
// customer picker on WZNew/WZDetail can never drift on which Customer field
// feeds which recipient input. The Customer entity only stores one free-text
// `address` field (no separate city/postalCode/country columns), so those
// breakdown inputs stay blank here and remain manually editable regardless of
// the source.
export function buildRecipientFieldsFromCustomer(customer?: Customer | null) {
  return {
    recipientName: customer?.name || '',
    recipientCompany: '',
    recipientAddress: customer?.address || '',
    recipientCity: '',
    recipientPostalCode: '',
    recipientCountry: '',
    recipientPhone: customer?.phone || '',
    recipientEmail: customer?.email || '',
    recipientNIP: customer?.taxId || '',
  };
}

export function mapOrderProductsToWZItems(order: { products?: Array<{
  productVariantId?: string | null;
  name?: string;
  productName?: string;
  cnCode?: string;
  quantity?: number;
  unit?: string;
  grossWeight?: number;
  netWeight?: number;
  unitPrice?: number;
  totalPrice?: number;
  description?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  productCategoryDimensionDisplayProfile?: string | null;
}> }): Partial<WZItem>[] {
  return (order.products || []).map(product => ({
    // Required so a line built this way can be recognised by the backend's
    // order↔WZ coverage matching (backend/src/utils/wzOrderLineCoverage.js),
    // which keys a variant-linked order line as `v:<id>` — an item with no
    // productVariantId can only ever match by name+code, and would silently
    // become invisible to coverage for a variant-linked line. See
    // frontend/src/pages/WZNew.tsx, which no longer builds its wizard items
    // from this function for exactly that reason and instead reads
    // GET /wz/prefill/:orderId (already netted, already variant-linked).
    productVariantId: product.productVariantId || undefined,
    productName: product.productName || product.name,
    productCode: product.cnCode || '',
    quantity: normalizeWZQuantity(product.quantity),
    // Order lines may carry the raw ProductUnit enum ('PIECE'); normalize to a
    // WZ code so the unit Select matches an option and shows the Polish label.
    unit: toWzUnitCode(product.unit),
    weight: product.grossWeight || product.netWeight,
    netWeight: product.netWeight,
    grossWeight: product.grossWeight,
    unitPrice: product.unitPrice,
    totalValue: product.totalPrice,
    notes: product.description,
    // Dimensions feed the live m²/m³ conversion shown next to the pieces.
    width: product.width,
    height: product.height,
    length: product.length,
    thickness: product.thickness,
    productCategoryDimensionDisplayProfile: product.productCategoryDimensionDisplayProfile ?? null,
  }));
}

function normalizeWZOrderProducts(raw: unknown): WZOrderProductDimensions[] {
  return asArray(raw, (item) => {
    const record = asRecord(item);
    return {
      name: asString(record?.name),
      unit: asOptionalString(record?.unit),
      width: record?.width == null ? undefined : asNumber(record.width),
      height: record?.height == null ? undefined : asNumber(record.height),
      length: record?.length == null ? undefined : asNumber(record.length),
      thickness: record?.thickness == null ? undefined : asNumber(record.thickness),
      productCategoryDimensionDisplayProfile: asOptionalString(record?.productCategoryDimensionDisplayProfile) ?? null,
    };
  });
}

// Attach the linked order's product dimensions to saved WZ items so the form
// can show the live m²/m³ conversion. Matches the backend PDF's order-product
// resolution (buildWzItems): by position first, then by product name.
//
// Deliberate limitation: the form resolves dimensions ONLY from order products.
// The PDF is authoritative and additionally derives dimensions parsed from the
// product-name text, so a line whose dimensions live only in its name (e.g. a
// manual WZ with no linked order) shows blank in the form yet still converts on
// the PDF. Order-linked lines — the normal flow — agree in both places.
export function attachWZItemDimensions(
  items: WZItem[],
  orderProducts: WZOrderProductDimensions[],
): WZItem[] {
  return items.map((item, index) => {
    const match = orderProducts[index] || orderProducts.find(p => p.name === item.productName);
    if (!match) {
      return item;
    }
    return {
      ...item,
      width: item.width ?? match.width,
      height: item.height ?? match.height,
      length: item.length ?? match.length,
      thickness: item.thickness ?? match.thickness,
      productCategoryDimensionDisplayProfile:
        item.productCategoryDimensionDisplayProfile
        ?? match.productCategoryDimensionDisplayProfile
        ?? null,
    };
  });
}

function normalizeWZOrder(raw: unknown, entity: string): WZDocument['order'] {
  const record = asRecord(raw);

  if (!record) {
    if (raw != null) {
      reportDtoAnomaly(entity, 'shape', raw);
    }
    return null;
  }

  const customerRecord = asRecord(record.customer);
  const orderNumber = asString(record.orderNumber);
  const manualOrderNumber = asOptionalString(record.manualOrderNumber) ?? null;
  const useManualNumbering = record.useManualNumbering === true;

  return {
    orderNumber,
    manualOrderNumber,
    useManualNumbering,
    displayOrderNumber:
      asOptionalString(record.displayOrderNumber)
      || getDisplayOrderNumber({ orderNumber, manualOrderNumber, useManualNumbering }),
    clientOrderNumber: asOptionalString(record.clientOrderNumber),
    customer: customerRecord
      ? {
          name: asString(customerRecord.name),
          company: asOptionalString(customerRecord.company),
        }
      : null,
  };
}

function normalizeCreatedBy(raw: unknown, entity: string): WZDocument['createdBy'] {
  const record = asRecord(raw);

  if (!record) {
    if (raw != null) {
      reportDtoAnomaly(entity, 'shape', raw);
    }
    return undefined;
  }

  return {
    email: asString(record.email),
    firstName: asOptionalString(record.firstName) || asOptionalString(record.name),
    lastName: asOptionalString(record.lastName),
  };
}

function normalizeWZItem(raw: unknown, entity: string): WZItem {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const netWeight = record?.netWeight == null ? undefined : Number(record.netWeight);
  const grossWeight = record?.grossWeight == null ? undefined : Number(record.grossWeight);
  const legacyWeight = record?.weight == null ? undefined : Number(record.weight);
  const conversionUnit = normalizeConversionUnit(record?.conversionUnit);
  const persistedConversionQuantity = getPersistedQuantityForUnit(record, conversionUnit);

  return {
    id: asString(record?.id),
    wzDocumentId: asString(record?.wzDocumentId),
    productName: asString(record?.productName),
    productCode: asOptionalString(record?.productCode),
    quantity: Number(record?.quantity || 0),
    quantityPcs: record?.quantityPcs == null ? null : Number(record.quantityPcs),
    quantityM2: record?.quantityM2 == null ? null : Number(record.quantityM2),
    quantityM3: record?.quantityM3 == null ? null : Number(record.quantityM3),
    // Existing WZ items may have been saved with the raw ProductUnit enum
    // (e.g. 'PIECE' inherited from an order); normalize to a WZ code on load.
    unit: toWzUnitCode(asOptionalString(record?.unit)),
    weight: legacyWeight ?? grossWeight ?? netWeight,
    netWeight,
    grossWeight,
    unitPrice: record?.unitPrice == null ? undefined : Number(record.unitPrice),
    totalValue: record?.totalValue == null ? undefined : Number(record.totalValue),
    saleQuantity: record?.saleQuantity == null ? null : Number(record.saleQuantity),
    saleUnit: asOptionalString(record?.saleUnit) ?? null,
    productVariantId: asOptionalString(record?.productVariantId),
    notes: asOptionalString(record?.notes),
    conversionUnit,
    conversionQuantity: record?.conversionQuantity == null
      ? (persistedConversionQuantity == null ? undefined : Number(persistedConversionQuantity))
      : Number(record.conversionQuantity),
    // Persisted per line since migration 096 (see types/wz.ts WZItem comment).
    width: record?.width == null ? undefined : Number(record.width),
    height: record?.height == null ? undefined : Number(record.height),
    length: record?.length == null ? undefined : Number(record.length),
    thickness: record?.thickness == null ? undefined : Number(record.thickness),
    productCategoryDimensionDisplayProfile:
      asOptionalString(record?.productCategoryDimensionDisplayProfile) ?? null,
  };
}

function normalizeWZAttachment(raw: unknown, entity: string): WZAttachment {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const uploadedByRecord = asRecord(record?.uploadedBy) || asRecord(record?.user);

  return {
    id: asString(record?.id),
    wzDocumentId: asString(record?.wzDocumentId) || asString(record?.wzId),
    fileName: asString(record?.fileName),
    filePath: asString(record?.filePath),
    fileType: asString(record?.fileType),
    fileSize: Number(record?.fileSize || 0),
    uploadedAt: asString(record?.uploadedAt),
    uploadedBy: uploadedByRecord
      ? {
          email: asString(uploadedByRecord.email),
        }
      : undefined,
  };
}

function normalizeWZStatus(value: unknown, entity: string): WZStatus {
  const normalizedValue = value === 'SENT' ? WZStatus.RELEASED : value;

  if (typeof normalizedValue === 'string' && WZ_STATUSES.includes(normalizedValue as WZStatus)) {
    return normalizedValue as WZStatus;
  }

  reportDtoAnomaly(entity, 'value', value);
  return WZStatus.DRAFT;
}
